import { useState, useEffect, useCallback, useRef } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
import { trackPageView } from "../../services/telemetry";
import { useUnsavedChangesContext } from "../../contexts/UnsavedChangesContext";
import {
  logout,
  selectTherapistProfile,
  selectClinicProfile,
  selectIsAdmin,
  selectIsNiVUser,
  selectIsNivAdmin,
  selectIsClinicInactiveByAdmin,
  selectIsTrialExpired,
  selectShouldShowPaywall,
  selectTrialDaysRemaining,
  selectShouldShowTrialWarning,
  selectCanAccessFinance,
  selectIsNutritionClinic,
  selectIsSecretary,
  selectHasPermission,
} from "../../store/slices/authSlice";
// import { startOnboarding } from "../../store/slices/uiSlice"; // COMMENTED OUT - replaced by contextual help system
import TrialContactModal from "./TrialContactModal";
import TrialUsageBanner from "./TrialUsageBanner";
import ConfirmDialog from "../ui/ConfirmDialog";
import {
  House,
  Users,
  Calendar,
  ClipboardText,
  CurrencyCircleDollar,
  Gear,
  SignOut,
  ClockCounterClockwise,
  CaretLeft,
  CaretRight,
  CaretDown,
  CaretUp,
  List,
  Clock,
  ChartLineUp,
  ForkKnife,
  Leaf,
  UsersThree,
  CalendarCheck,
  ListChecks,
  Buildings,
  Crown,
  Toolbox,
  Lifebuoy,
  Headset,
  DeviceMobile,
} from "@phosphor-icons/react";
import logoIcon from "../../assets/logo-icon.png";
import { LOGGED_IN_APP_TITLE } from "../../constants/appDocumentTitle";
import NotificationsDropdown from "./NotificationsDropdown";
import packageJson from "../../../package.json";
import MobileLiveSessionBanner from "./MobileLiveSessionBanner";
import SupportChatWidget from "../support/SupportChatWidget";
import { openLenny } from "../support/lennyBus";
import lennyIcon from "../../assets/lenny-icon-512.jpg";
// import { OnboardingTour } from "../onboarding"; // COMMENTED OUT - replaced by contextual help system
import { WelcomeOnboarding, triggerWelcomeOnboarding } from "../onboarding";
import TeamWelcomeModal from "../team/TeamWelcomeModal";
// import EmailLinkPrompt from "./EmailLinkPrompt"; // MOVED to SettingsPage (manual button for whitelisted users)
import PushNotificationPrompt from "./PushNotificationPrompt";
import PwaInstallPrompt, { openPwaInstallPrompt } from "./PwaInstallPrompt";
import { getDeviceType, isStandalonePWA } from "../../utils/deviceUtils";
import SystemMessagePopup from "./SystemMessagePopup";
import "./DashboardLayout.css";

const menuItems = [
  {
    id: "home",
    label: "דף הבית",
    icon: House,
    path: "/dashboard",
  },
  {
    id: "patients",
    label: "המטופלים שלי",
    icon: UsersThree,
    path: "/dashboard/patients",
  },
  {
    // Multi-clinic v2 — non-admin with patients.viewAllClinic only
    id: "patients-all",
    label: "מטופלי הקליניקה",
    icon: UsersThree,
    path: "/dashboard/patients/all",
  },
  {
    // Nutrition clinics only — filtered out everywhere else
    id: "nutrition",
    label: "מרכז הליווי",
    icon: Leaf,
    path: "/dashboard/nutrition",
  },
  {
    id: "calendar",
    label: "יומן ותורים",
    icon: CalendarCheck,
    path: "/dashboard/calendar",
  },
  {
    // Multi-clinic v2 — non-admin with calendar.viewClinic only
    id: "clinic-calendar",
    label: "יומן הקליניקה",
    icon: CalendarCheck,
    path: "/dashboard/clinic-calendar",
  },
  {
    id: "toolbox",
    label: "ארגז הכלים",
    icon: Toolbox,
    path: "/dashboard/tasks",
  },
  {
    id: "finance",
    label: "פיננסים ותשלומים",
    icon: CurrencyCircleDollar,
    path: "/dashboard/finance",
  },
  {
    id: "reports",
    label: "דוחות",
    icon: ChartLineUp,
    path: "/dashboard/reports",
  },
  {
    // Nutrition clinics only — filtered out everywhere else
    id: "foods",
    label: "מאגר המזונות",
    icon: ForkKnife,
    path: "/dashboard/foods",
  },
];

// Base admin menu items
const baseAdminMenuItems = [
  {
    id: "admin-snapshot",
    label: "תמונת מצב",
    icon: ChartLineUp,
    path: "/dashboard/admin/snapshot",
  },
  {
    id: "admin-patients",
    label: "ניהול מטופלים",
    icon: UsersThree,
    path: "/dashboard/admin/patients",
  },
  {
    id: "admin-calendar",
    label: "יומנים ותורים",
    icon: CalendarCheck,
    path: "/dashboard/admin/calendar",
  },
  {
    id: "admin-tasks",
    label: "ניהול משימות ותזכורות",
    icon: ListChecks,
    path: "/dashboard/admin/tasks",
  },
  {
    id: "admin-team",
    label: "צוות הקליניקה",
    icon: UsersThree,
    path: "/dashboard/admin/team",
  },
  {
    id: "admin-activity",
    label: "יומן פעילות",
    icon: ClockCounterClockwise,
    path: "/dashboard/admin/activity-logs",
  },
];

// Helper to get today's date string for localStorage key
const getTodayDateKey = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(today.getDate()).padStart(2, "0")}`;
};

// LocalStorage key for trial warning acknowledgment
const TRIAL_WARNING_ACK_KEY = "clinikit_trial_warning_ack";

function DashboardLayout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const therapistProfile = useSelector(selectTherapistProfile);
  const clinicProfile = useSelector(selectClinicProfile);
  const isAdmin = useSelector(selectIsAdmin);
  const isNiVUser = useSelector(selectIsNiVUser);
  const isNivAdmin = useSelector(selectIsNivAdmin);
  const canAccessFinance = useSelector(selectCanAccessFinance);
  const isNutritionClinic = useSelector(selectIsNutritionClinic);
  // Multi-clinic v2 — legacy therapist docs (no `permissions` map) resolve all
  // of these to false, so their nav is unchanged.
  const isSecretary = useSelector(selectIsSecretary);
  const canViewClinicCalendar = useSelector(selectHasPermission("calendar.viewClinic"));
  const canViewAllPatients = useSelector(selectHasPermission("patients.viewAllClinic"));
  const canViewClinicWideReports = useSelector(selectHasPermission("reports.viewClinicWide"));

  useEffect(() => {
    document.title = LOGGED_IN_APP_TITLE;
  }, []);

  // Unsaved changes context
  const {
    hasUnsavedChanges,
    requestNavigation,
    confirmNavigation,
    cancelNavigation,
    showDialog: showUnsavedChangesDialog,
  } = useUnsavedChangesContext();

  // Trial status selectors
  const isTrialExpired = useSelector(selectIsTrialExpired);
  const shouldShowPaywall = useSelector(selectShouldShowPaywall);
  const isClinicInactiveByAdmin = useSelector(selectIsClinicInactiveByAdmin);
  const trialDaysRemaining = useSelector(selectTrialDaysRemaining);
  const shouldShowTrialWarning = useSelector(selectShouldShowTrialWarning);

  // TEMP TRIAL DEBUG — remove after diagnosing the popup. Prints the exact
  // values the app is computing from, so we can see why a popup shows.
  const _trialDebugClinic = useSelector((s) => s.auth.clinicProfile);
  useEffect(() => {
    const raw = _trialDebugClinic?.trialEndDate;
    const parsed = raw ? (raw.toDate ? raw.toDate() : new Date(raw)) : null;
    // eslint-disable-next-line no-console
    console.log("[TRIAL DEBUG]", {
      plan: _trialDebugClinic?.plan,
      rawTrialEndDate: raw,
      rawType: raw && raw.toDate ? "Timestamp" : typeof raw,
      parsedTrialEndDate: parsed ? parsed.toString() : null,
      now: new Date().toString(),
      trialDaysRemaining,
      isTrialExpired,
      subscriptionEndDate: _trialDebugClinic?.subscriptionEndDate,
      shouldShowPaywall,
      shouldShowTrialWarning,
    });
  }, [
    _trialDebugClinic,
    trialDaysRemaining,
    isTrialExpired,
    shouldShowPaywall,
    shouldShowTrialWarning,
  ]);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Auto-collapse the sidebar when entering the calendar page, per the
  // therapist preference `minimizeMenuOnCalendar`. Missing property means
  // enabled (backward compatible with existing production therapist docs).
  // Desktop only — the mobile drawer renders nav labels conditionally on
  // the collapsed flag, so collapsing there shows an icons-only menu.
  const prevPathRef = useRef(null);
  useEffect(() => {
    const wasCalendar = prevPathRef.current?.startsWith("/dashboard/calendar");
    const isCalendar = location.pathname.startsWith("/dashboard/calendar");
    prevPathRef.current = location.pathname;
    if (
      isCalendar &&
      !wasCalendar &&
      window.innerWidth > 768 &&
      therapistProfile?.minimizeMenuOnCalendar !== false
    ) {
      setIsSidebarCollapsed(true);
      setNavTooltip(null);
    }
  }, [location.pathname, therapistProfile?.minimizeMenuOnCalendar]); // eslint-disable-line react-hooks/exhaustive-deps
  const [isAdminSectionCollapsed, setIsAdminSectionCollapsed] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  // Trial warning acknowledgment state
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);

  // Logout confirmation dialog state
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [navTooltip, setNavTooltip] = useState(null);

  const showNavTooltip = (e, label) => {
    if (!isSidebarCollapsed || !label) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setNavTooltip({ label, top: rect.top + rect.height / 2 });
  };

  const hideNavTooltip = () => setNavTooltip(null);

  // Safe navigation handler - checks for unsaved changes before navigating
  const handleSafeNavigation = useCallback(
    (e, path) => {
      // If already on this path, don't block
      if (location.pathname === path) {
        return;
      }

      // If there are unsaved changes, block navigation and show dialog
      if (hasUnsavedChanges) {
        e.preventDefault();
        requestNavigation(() => navigate(path));
      }
      // Otherwise, let the NavLink handle navigation normally
      setIsMobileSidebarOpen(false);
    },
    [hasUnsavedChanges, requestNavigation, navigate, location.pathname],
  );

  // Check if warning was already acknowledged today
  useEffect(() => {
    if (shouldShowTrialWarning) {
      const stored = localStorage.getItem(TRIAL_WARNING_ACK_KEY);
      if (stored) {
        try {
          const data = JSON.parse(stored);
          const todayKey = getTodayDateKey();
          // Check if acknowledged today for the current days remaining
          if (
            data.date === todayKey &&
            data.daysRemaining === trialDaysRemaining
          ) {
            setWarningAcknowledged(true);
          } else {
            setWarningAcknowledged(false);
          }
        } catch {
          setWarningAcknowledged(false);
        }
      }
    }
  }, [shouldShowTrialWarning, trialDaysRemaining]);

  // Handle warning acknowledgment
  const handleWarningAcknowledge = useCallback(() => {
    const todayKey = getTodayDateKey();
    localStorage.setItem(
      TRIAL_WARNING_ACK_KEY,
      JSON.stringify({
        date: todayKey,
        daysRemaining: trialDaysRemaining,
      }),
    );
    setWarningAcknowledged(true);
  }, [trialDaysRemaining]);

  // Get therapist full name for modals
  const therapistFullName = therapistProfile
    ? `${therapistProfile.firstName || ""} ${
        therapistProfile.lastName || ""
      }`.trim()
    : null;

  // Admin menu items (static list)
  const adminMenuItems = baseAdminMenuItems;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Track page views for observability
  useEffect(() => {
    // Batched telemetry (aggregated daily counters → niv_telemetry_daily_therapist).
    // The legacy 1-doc-per-view niv_page_analytics write was removed with the
    // old page_analytics NiV tab (telemetry rollout Phase 4).
    trackPageView(location.pathname);
  }, [location.pathname]);

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await signOut(auth);
      dispatch(logout());
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
    setNavTooltip(null);
  };

  const toggleMobileSidebar = () => {
    // The mobile drawer must always be the full menu — never icons-only
    // (nav labels are conditionally rendered on the collapsed flag).
    if (!isMobileSidebarOpen) setIsSidebarCollapsed(false);
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("he-IL", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserDropdownOpen && !event.target.closest(".user-menu-container")) {
        setIsUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isUserDropdownOpen]);

  // Handle navigation to settings from user dropdown
  const handleNavigateToSettings = (e) => {
    setIsUserDropdownOpen(false);
    if (hasUnsavedChanges) {
      e.preventDefault();
      requestNavigation(() => navigate("/dashboard/settings"));
    } else {
      navigate("/dashboard/settings");
    }
  };

  // Handle navigation to support center from user dropdown
  const handleNavigateToSupport = (e) => {
    setIsUserDropdownOpen(false);
    if (hasUnsavedChanges) {
      e.preventDefault();
      requestNavigation(() => navigate("/dashboard/support"));
    } else {
      navigate("/dashboard/support");
    }
  };

  // Handle navigation to clinic settings from user dropdown (admin only)
  const handleNavigateToClinicSettings = (e) => {
    setIsUserDropdownOpen(false);
    if (hasUnsavedChanges) {
      e.preventDefault();
      requestNavigation(() => navigate("/dashboard/admin/settings"));
    } else {
      navigate("/dashboard/admin/settings");
    }
  };

  return (
    <div className="dashboard-layout">
      {/* Mobile Overlay */}
      {isMobileSidebarOpen && (
        <div className="sidebar-overlay" onClick={toggleMobileSidebar}></div>
      )}

      {/* Collapsed-sidebar hover tooltip */}
      {navTooltip && isSidebarCollapsed && (
        <div
          className="sidebar-nav-tooltip"
          style={{ top: navTooltip.top }}
          role="tooltip"
        >
          {navTooltip.label}
        </div>
      )}

      {/* Sidebar */}
      <aside
        className={`dashboard-sidebar ${
          isSidebarCollapsed ? "collapsed" : ""
        } ${isMobileSidebarOpen ? "mobile-open" : ""} ${
          isAdmin && !clinicProfile?.isSoloClinic ? "has-admin-section" : ""
        }`}
      >
        <div className="sidebar-header">
          <NavLink
            to="/dashboard"
            end
            className="sidebar-logoo"
            aria-label="דף הבית"
            onClick={() => setIsMobileSidebarOpen(false)}
          >
            {!isSidebarCollapsed && <span className="logo-text">CliniKit</span>}
            <img src={logoIcon} alt="CliniKit" className="logo-img" />
          </NavLink>
        </div>

        <nav className="sidebar-nav">
          {/* NiV Dashboard Menu Item - Only for SaaS Admin (Custom Claims) */}
          {(isNivAdmin || isNiVUser) && (
            <ul className="nav-list niv-nav-list">
              <li>
                <NavLink
                  to="/dashboard/niv"
                  className={({ isActive }) =>
                    `nav-item niv-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={(e) => handleSafeNavigation(e, "/dashboard/niv")}
                  onMouseEnter={(e) => showNavTooltip(e, "ניב דשבורד")}
                  onMouseLeave={hideNavTooltip}
                >
                  <span className="nav-icon">
                    <Crown size={22} weight="fill" />
                  </span>
                  {!isSidebarCollapsed && (
                    <span className="nav-label">ניב דשבורד</span>
                  )}
                </NavLink>
              </li>
            </ul>
          )}

          <ul className="nav-list">
            {menuItems
              .filter((item) => item.id !== "finance" || canAccessFinance)
              // Multi-clinic v2: clinic-wide calendar / patients entries exist
              // only for non-admins holding the matching permission (admins
              // have the admin section for these)
              .filter(
                (item) =>
                  item.id !== "clinic-calendar" ||
                  (!isAdmin && canViewClinicCalendar === true)
              )
              .filter(
                (item) =>
                  item.id !== "patients-all" ||
                  (!isAdmin && canViewAllPatients === true)
              )
              // Secretary: no personal calendar / patients of their own
              .filter(
                (item) =>
                  !isSecretary ||
                  (item.id !== "calendar" && item.id !== "patients")
              )
              // Reports: owners/admins always; therapists only in
              // multi-therapist clinics (scoped to their own data there);
              // secretaries only with reports.viewClinicWide
              .filter(
                (item) =>
                  item.id !== "reports" ||
                  isAdmin ||
                  (isSecretary
                    ? canViewClinicWideReports === true
                    : !clinicProfile?.isSoloClinic)
              )
              // Coaching workspace + food catalog exist only for nutrition clinics
              .filter(
                (item) =>
                  (item.id !== "foods" && item.id !== "nutrition") ||
                  isNutritionClinic
              )
              .map((item) => (
                <li key={item.id}>
                  <NavLink
                    to={item.path}
                    end={item.path === "/dashboard"}
                    className={({ isActive }) =>
                      `nav-item ${isActive ? "active" : ""}`
                    }
                    onClick={(e) => handleSafeNavigation(e, item.path)}
                    onMouseEnter={(e) => showNavTooltip(e, item.label)}
                    onMouseLeave={hideNavTooltip}
                  >
                    <span className="nav-icon">
                      <item.icon size={22} weight="regular" />
                    </span>
                    {!isSidebarCollapsed && (
                      <span className="nav-label">{item.label}</span>
                    )}
                  </NavLink>
                </li>
              ))}
          </ul>

          {/* Admin Section - Hidden for solo clinics */}
          {isAdmin && !clinicProfile?.isSoloClinic && (
            <>
              <div className="nav-section-divider"></div>
              {!isSidebarCollapsed && (
                <button
                  className="nav-section-title"
                  onClick={() =>
                    setIsAdminSectionCollapsed(!isAdminSectionCollapsed)
                  }
                >
                  <span>ניהול הקליניקה</span>
                  {isAdminSectionCollapsed ? (
                    <CaretDown size={14} weight="bold" />
                  ) : (
                    <CaretUp size={14} weight="bold" />
                  )}
                </button>
              )}
              {!isAdminSectionCollapsed && (
                <ul className="nav-list admin-nav-list">
                  {adminMenuItems.map((item) => (
                    <li key={item.id}>
                      <NavLink
                        to={item.path}
                        className={({ isActive }) =>
                          `nav-item admin-nav-item ${isActive ? "active" : ""}`
                        }
                        onClick={(e) => handleSafeNavigation(e, item.path)}
                        onMouseEnter={(e) => showNavTooltip(e, item.label)}
                        onMouseLeave={hideNavTooltip}
                      >
                        <span className="nav-icon">
                          <item.icon size={22} weight="regular" />
                        </span>
                        {!isSidebarCollapsed && (
                          <span className="nav-label">{item.label}</span>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <button className="sidebar-toggle" onClick={toggleSidebar}>
            <span className="nav-icon">
              {isSidebarCollapsed ? (
                <CaretLeft size={20} weight="bold" />
              ) : (
                <CaretRight size={20} weight="bold" />
              )}
            </span>
            {!isSidebarCollapsed && (
              <span className="nav-label">כווץ תפריט</span>
            )}
          </button>

          {!isSidebarCollapsed && (
            <button
              className="app-version"
              onClick={() => window.location.reload(true)}
              title="לחץ לעדכון גרסה"
            >
              v{packageJson.version}
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className="dashboard-main">
        {/* Top Header */}
        <header className="dashboard-header">
          <button className="mobile-menu-btn" onClick={toggleMobileSidebar}>
            <List size={24} weight="bold" />
          </button>

          {/* User Menu - Right Side */}
          <div className="user-menu-container">
            <button
              className={`user-menu ${isUserDropdownOpen ? "active" : ""}`}
              onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
            >
              <div className="user-logo">
                {clinicProfile?.logoUrl ? (
                  <img src={clinicProfile.logoUrl} alt="Clinic" />
                ) : (
                  <div className="user-avatar">
                    <span>{therapistProfile?.firstName?.charAt(0) || "U"}</span>
                  </div>
                )}
              </div>
              <div className="user-info">
                <span className="user-name">
                  {therapistProfile?.firstName} {therapistProfile?.lastName}
                </span>
                <span className="clinic-name">{clinicProfile?.name}</span>
              </div>
              <CaretDown
                size={16}
                weight="bold"
                className={`user-menu-caret ${isUserDropdownOpen ? "rotated" : ""}`}
              />
            </button>

            {/* User Dropdown */}
            {isUserDropdownOpen && (
              <div className="user-dropdown">
                <button
                  className="user-dropdown-item"
                  onClick={handleNavigateToSettings}
                >
                  <Gear size={18} weight="regular" />
                  <span>הגדרות מטפל</span>
                </button>
                {isAdmin && (
                  <button
                    className="user-dropdown-item"
                    onClick={handleNavigateToClinicSettings}
                  >
                    <Buildings size={18} weight="regular" />
                    <span>הגדרות קליניקה</span>
                  </button>
                )}
                {/* Multi-clinic v2: every therapist sees their own activity rows */}
                {!isAdmin && (
                  <button
                    className="user-dropdown-item"
                    onClick={(e) => {
                      setIsUserDropdownOpen(false);
                      if (hasUnsavedChanges) {
                        e.preventDefault();
                        requestNavigation(() => navigate("/dashboard/activity"));
                      } else {
                        navigate("/dashboard/activity");
                      }
                    }}
                  >
                    <ClockCounterClockwise size={18} weight="regular" />
                    <span>הפעילות שלי</span>
                  </button>
                )}
                <div className="user-dropdown-divider" />
                <button
                  className="user-dropdown-item"
                  onClick={() => {
                    setIsUserDropdownOpen(false);
                    triggerWelcomeOnboarding();
                  }}
                >
                  <Lifebuoy size={18} weight="regular" />
                  <span>הפעל הדרכה</span>
                </button>
                {/* Mobile browser tab only — hidden on desktop and inside the installed PWA */}
                {getDeviceType() === "mobile" && !isStandalonePWA() && (
                  <button
                    className="user-dropdown-item"
                    onClick={() => {
                      setIsUserDropdownOpen(false);
                      openPwaInstallPrompt();
                    }}
                  >
                    <DeviceMobile size={18} weight="regular" />
                    <span>התקנת האפליקציה</span>
                  </button>
                )}
                <button
                  className="user-dropdown-item logout-item"
                  onClick={() => {
                    setIsUserDropdownOpen(false);
                    handleLogoutClick();
                  }}
                >
                  <SignOut size={18} weight="regular" />
                  <span>התנתק</span>
                </button>
              </div>
            )}
          </div>

          {/* Center - Empty placeholder (indicator moved to bottom banner) */}
          <div className="header-center"></div>

          {/* Header Actions - Left Side */}
          <div className="header-actions">
            {/* Clock & Date */}
            <div className="header-datetime">
              <div className="header-time">
                <Clock size={16} weight="regular" />
                <span>{formatTime(currentTime)}</span>
              </div>
              <span className="header-date">{formatDate(currentTime)}</span>
            </div>

            <div className="header-divider"></div>

            {/* Lenny chat — mobile-only header entry (the floating FAB is hidden ≤768px).
                RTL: first in DOM = visually to the RIGHT of the support button */}
            <button
              type="button"
              className="header-lenny-btn"
              onClick={openLenny}
              title="לני — צ'אט תמיכה"
              aria-label="לני — צ'אט תמיכה"
            >
              <img src={lennyIcon} alt="" className="header-lenny-img" />
            </button>

            {/* Support Center Button */}
            <button
              className="header-support-btn"
              onClick={handleNavigateToSupport}
              title="מרכז התמיכה"
            >
              <Headset size={20} weight="regular" />
              <span className="support-btn-label">תמיכה</span>
            </button>

            <div className="header-divider"></div>

            <NotificationsDropdown />
          </div>
        </header>

        {/* Trial usage bar — days remaining + patients/SMS meters (trial clinics only) */}
        <TrialUsageBanner />

        {/* Page Content */}
        <main className="dashboard-content">
          <Outlet />
        </main>
      </div>

      {/* COMMENTED OUT - replaced by contextual help system */}
      {/* <OnboardingTour /> */}

      {/* Multi-clinic v2: invited member's first login (before the tour) */}
      <TeamWelcomeModal />

      {/* Welcome Onboarding — shown once for new users */}
      <WelcomeOnboarding />

      {/* Email Link Prompt - MOVED to SettingsPage as manual button for whitelisted users */}
      {/* <EmailLinkPrompt /> */}

      {/* Push Notification Prompt — auto-prompt on 3rd day + token refresh + foreground listener */}
      <PushNotificationPrompt />

      {/* Contact popup (blocking) - clinic deactivated by system admin. Takes
          precedence over the trial/subscription paywall. */}
      {isClinicInactiveByAdmin && (
        <TrialContactModal variant="disabled" therapistName={therapistFullName} />
      )}

      {/* Contact popup (blocking) - shown when trial OR paid subscription has lapsed */}
      {!isClinicInactiveByAdmin && shouldShowPaywall && (
        <TrialContactModal variant="expired" therapistName={therapistFullName} />
      )}

      {/* Contact popup (dismissible) - 3, 2, 1 days before trial expiration */}
      {!isClinicInactiveByAdmin && !shouldShowPaywall && shouldShowTrialWarning && !warningAcknowledged && (
        <TrialContactModal
          variant="warning"
          daysRemaining={trialDaysRemaining}
          therapistName={therapistFullName}
          onAcknowledge={handleWarningAcknowledge}
        />
      )}

      {/* System message broadcast popup — only when no blocking paywall is up */}
      {!shouldShowPaywall && !isClinicInactiveByAdmin && <SystemMessagePopup />}

      {/* "Install CliniKit" banner — mobile users browsing in a regular tab (not the PWA) */}
      {!shouldShowPaywall && !isClinicInactiveByAdmin && <PwaInstallPrompt />}

      {/* Logout Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        onConfirm={handleLogoutConfirm}
        title="התנתקות"
        message="האם אתה בטוח שברצונך להתנתק?"
        confirmText="התנתק"
        cancelText="ביטול"
        type="warning"
        icon="warning"
      />

      {/* Unsaved Changes Warning Dialog */}
      <ConfirmDialog
        isOpen={showUnsavedChangesDialog}
        onClose={cancelNavigation}
        onConfirm={confirmNavigation}
        title="יש שינויים שלא נשמרו"
        message="יש שינויים שלא נשמרו - האם אתה בטוח שברצונך לצאת?"
        confirmText="כן, צא מהעמוד"
        cancelText="לא, חזור לעריכה"
        type="warning"
        icon="warning"
      />

      {/* Lenny AI support chat — portals to document.body, dashboard-only via this mount */}
      <SupportChatWidget />

      {/* Mobile Live Session Banner - Bottom fixed banner for mobile */}
      <MobileLiveSessionBanner />
    </div>
  );
}

export default DashboardLayout;
